import React, { useRef, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';

interface NeuralNetworkBackgroundProps {
  opacity?: number;
  animate?: boolean;
  color1?: string;
  color2?: string;
  nodeCount?: number;
  connectionCount?: number;
  animationSpeed?: number;
}

const MotionBox = motion(Box);

const NeuralNetworkBackground: React.FC<NeuralNetworkBackgroundProps> = ({
  opacity = 0.1,
  animate = true,
  color1 = '#0066CC', // Methodist Blue
  color2 = '#4A8B71', // Healing Green
  nodeCount = 50,
  connectionCount = 75,
  animationSpeed = 1
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions to match parent
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Node class
    class Node {
      x: number;
      y: number;
      radius: number;
      color: string;
      vx: number;
      vy: number;
      
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.radius = Math.random() * 2 + 1;
        this.color = Math.random() > 0.5 ? color1 : color2;
        this.vx = (Math.random() - 0.5) * 0.3 * animationSpeed;
        this.vy = (Math.random() - 0.5) * 0.3 * animationSpeed;
      }
      
      update() {
        if (animate) {
          this.x += this.vx;
          this.y += this.vy;
          
          // Bounce off walls
          if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
          if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
        }
      }
      
      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = opacity * 0.7;
        ctx.fill();
      }
    }
    
    // Create nodes
    const nodes: Node[] = [];
    for (let i = 0; i < nodeCount; i++) {
      nodes.push(new Node());
    }
    
    // Draw connections between nodes
    const drawConnections = () => {
      if (!ctx) return;
      
      for (let i = 0; i < connectionCount; i++) {
        const nodeA = nodes[Math.floor(Math.random() * nodes.length)];
        const nodeB = nodes[Math.floor(Math.random() * nodes.length)];
        
        const distance = Math.sqrt(
          Math.pow(nodeB.x - nodeA.x, 2) + Math.pow(nodeB.y - nodeA.y, 2)
        );
        
        if (distance < canvas.width / 4) {
          ctx.beginPath();
          ctx.moveTo(nodeA.x, nodeA.y);
          ctx.lineTo(nodeB.x, nodeB.y);
          ctx.strokeStyle = Math.random() > 0.5 ? color1 : color2;
          ctx.globalAlpha = opacity * 0.2 * (1 - distance / (canvas.width / 4));
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    };
    
    // Animation loop
    const animate = () => {
      if (!ctx) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw nodes
      nodes.forEach(node => {
        node.update();
        node.draw();
      });
      
      // Draw connections
      drawConnections();
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationRef.current);
    };
  }, [opacity, animate, color1, color2, nodeCount, connectionCount, animationSpeed]);
  
  return (
    <MotionBox
      position="absolute"
      top="0"
      left="0"
      width="100%"
      height="100%"
      zIndex="0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }}
      />
    </MotionBox>
  );
};

export default NeuralNetworkBackground;
